import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import useReactApexChart from "../../hook/useReactApexChart";
import { api } from "../../services/api";

const RevenueStatsChart = () => {
  const [revenueData, setRevenueData] = useState({
    totalRevenue: { totalRevenue: 0 },
    projectedMonthlyRevenue: 0,
    currentMonthRevenue: { totalRevenue: 0 },
    monthlyRevenue: [],
    loading: true,
    error: false,
  });

  // Get the default chart options from hook
  let { lineChartOptions } = useReactApexChart();

  // Customize options for revenue chart
  const chartOptions = {
    ...lineChartOptions,
    colors: ["#2E93fA", "#66DA26", "#546E7A"],
    title: {
      text: "Monthly Revenue (Last 6 Months)",
      align: "left",
      style: {
        fontSize: "14px",
        fontWeight: 500,
      },
    },
    xaxis: {
      categories: [], // Will be filled with month names
    },
    yaxis: {
      title: {
        text: "Revenue (EUR)",
      },
    },
  };

  // Default series structure
  const [chartSeries, setChartSeries] = useState([
    {
      name: "Revenue",
      data: [], // Will be filled with revenue data
    },
  ]);

  useEffect(() => {
    const fetchRevenueData = async () => {
      try {
        // Fetch revenue stats
        const response = await api.subscriptions.getRevenueStats();

        if (response.success && response.data) {
          // Extract data from the API response
          const {
            totalRevenue,
            projectedMonthlyRevenue,
            currentMonthRevenue,
            monthlyRevenue,
          } = response.data;

          console.log("API Response:", response.data);

          // Format monthly data for the chart
          if (monthlyRevenue && monthlyRevenue.length > 0) {
            // Extract month names and revenue values
            const months = monthlyRevenue.map((item) => item.monthName);
            const values = monthlyRevenue.map((item) => {
              // Add proper null checking for the revenue object and totalRevenue property
              return item.totalRevenue || 0;
            });

            // Update chart options with month names
            chartOptions.xaxis.categories = months;

            // Update chart series with revenue data
            setChartSeries([
              {
                name: "Monthly Revenue",
                data: values,
              },
            ]);
          }

          // Update state with the fetched data
          setRevenueData({
            totalRevenue: totalRevenue || { totalRevenue: 0 },
            projectedMonthlyRevenue: projectedMonthlyRevenue || 0,
            currentMonthRevenue: currentMonthRevenue || { totalRevenue: 0 },
            monthlyRevenue: monthlyRevenue || [],
            loading: false,
            error: false,
          });
        } else {
          // Handle API response error
          console.error("API returned an error:", response.message);
          setRevenueData({
            totalRevenue: { totalRevenue: 10000 }, // Fallback data
            projectedMonthlyRevenue: 1200,
            currentMonthRevenue: { totalRevenue: 850 },
            monthlyRevenue: [],
            loading: false,
            error: true,
          });
        }
      } catch (error) {
        console.error("Error fetching revenue data:", error);
        setRevenueData({
          totalRevenue: { totalRevenue: 10000 }, // Fallback data
          projectedMonthlyRevenue: 1200,
          currentMonthRevenue: { totalRevenue: 850 },
          monthlyRevenue: [],
          loading: false,
          error: true,
        });
      }
    };

    fetchRevenueData();
  }, []);

  // Format currency values
  const formatCurrency = (amount, currency = "EUR") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Calculate growth percentage
  const calculateGrowth = () => {
    if (revenueData.monthlyRevenue && revenueData.monthlyRevenue.length >= 2) {
      const currentMonth =
        revenueData.monthlyRevenue[revenueData.monthlyRevenue.length - 1];
      const previousMonth =
        revenueData.monthlyRevenue[revenueData.monthlyRevenue.length - 2];

      if (
        previousMonth &&
        previousMonth.totalRevenue > 0 &&
        currentMonth &&
        currentMonth.totalRevenue
      ) {
        return (
          ((currentMonth.totalRevenue - previousMonth.totalRevenue) /
            previousMonth.totalRevenue) *
          100
        );
      }
    }
    return 0;
  };

  const growthPercent = calculateGrowth();
  const isPositiveGrowth = growthPercent >= 0;
  const growthClass = isPositiveGrowth
    ? "bg-success-focus text-success-main"
    : "bg-danger-focus text-danger-main";
  const growthIcon = isPositiveGrowth
    ? "iconamoon:arrow-up-2-fill"
    : "iconamoon:arrow-down-2-fill";

  return (
    <div className="col-xxl-4 col-xl-8">
      <div className="card h-100 radius-8 border">
        <div className="card-body p-24">
          <h6 className="mb-12 fw-semibold text-lg mb-16">
            Revenue Statistics
          </h6>
          {revenueData.error && (
            <div className="text-sm text-danger-main mb-2">
              <Icon icon="mdi:alert-circle-outline" className="me-1" />
              Using demo data (API error)
            </div>
          )}

          <div className="row mb-20">
            <div className="col-md-4">
              <div className="d-flex flex-column">
                <span className="text-sm text-muted mb-1">Total Revenue</span>
                <h5 className="fw-semibold mb-0">
                  {revenueData.loading
                    ? "Loading..."
                    : formatCurrency(
                        revenueData.totalRevenue &&
                          revenueData.totalRevenue.totalRevenue
                          ? revenueData.totalRevenue.totalRevenue
                          : 0
                      )}
                </h5>
              </div>
            </div>

            <div className="col-md-4">
              <div className="d-flex flex-column">
                <span className="text-sm text-muted mb-1">Current Month</span>
                <h5 className="fw-semibold mb-0">
                  {revenueData.loading
                    ? "Loading..."
                    : formatCurrency(
                        revenueData.currentMonthRevenue &&
                          revenueData.currentMonthRevenue.totalRevenue
                          ? revenueData.currentMonthRevenue.totalRevenue
                          : 0
                      )}
                </h5>
                <p className="text-xs mb-0">
                  <span
                    className={`border px-6 py-1 rounded-pill fw-semibold d-inline-flex align-items-center gap-1 ${growthClass}`}
                  >
                    {Math.abs(growthPercent).toFixed(1)}%
                    <Icon icon={growthIcon} className="icon" />
                  </span>
                  vs last month
                </p>
              </div>
            </div>

            <div className="col-md-4">
              <div className="d-flex flex-column">
                <span className="text-sm text-muted mb-1">
                  Projected Monthly
                </span>
                <h5 className="fw-semibold mb-0">
                  {revenueData.loading
                    ? "Loading..."
                    : formatCurrency(revenueData.projectedMonthlyRevenue || 0)}
                </h5>
              </div>
            </div>
          </div>

          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="line"
            height={240}
          />
        </div>
      </div>
    </div>
  );
};

export default RevenueStatsChart;
